// frontend/src/ReleaseNotes/components/ReleaseNotesPublicList.tsx
import React from "react";
import { Badge, Box, Card, Divider, Group, Stack, Text, Title } from "@mantine/core";
import type { IBlobMedia, IDateish, IReleaseItem, IReleaseNote } from "@multiconsult-group/multiplacer-core";
import { BlobMediaPreview } from "./BlobMedia";
import { useAppSelector } from "../app/hooks";
import {
  selectReleaseNotesPublishedFiltered,
  selectReleaseNotesPublishedFilteredTotalCount,
} from "./releaseNotesSelectors";
import {
  RELEASE_IMPACT_LABEL,
  RELEASE_MODULE_LABEL,
  RELEASE_TYPE_LABEL,
} from "./releaseNotesUiSpecs";

const formatIsoDateOnly = (iso: IDateish): string => {
  // iso is e.g. "2026-02-02T12:34:56.000Z" -> "2026-02-02"
  // If iso already is "2026-02-02", this still works.
  return String(iso).slice(0, 10);
};

const ReleaseItemCard: React.FC<{ item: IReleaseItem }> = ({ item }) => {
  return (
    <Card withBorder radius="md" padding="sm">
      <Stack gap="xs">
        <Group gap="xs" wrap="wrap">
          <Badge variant="light">{RELEASE_TYPE_LABEL[item.type]}</Badge>
          <Badge variant="light">{RELEASE_MODULE_LABEL[item.module]}</Badge>
          {item.impact ? (
            <Badge variant="outline">{`Påvirkning: ${RELEASE_IMPACT_LABEL[item.impact]}`}</Badge>
          ) : null}
        </Group>

        {item.title ? (
          <Text size="md">
            <b>{item.title}</b>
          </Text>
        ) : null}

        <Text>{item.text}</Text>

        {item.media && item.media.length > 0 ? (
          <Stack gap="xs">
            {item.media.map((m: IBlobMedia) => (
              <Box key={m.blobPath}>
                <BlobMediaPreview media={m} target="release-notes" />
              </Box>
            ))}
          </Stack>
        ) : null}
      </Stack>
    </Card>
  );
};
const ReleaseCard: React.FC<{ release: IReleaseNote }> = ({ release }) => {
  const dateOnly = formatIsoDateOnly(release.releaseDate);

  return (
    <Card withBorder radius="lg" padding="md">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Stack gap={2}>
            <Title order={3}>{release.version}</Title>

            <Group gap="xs" wrap="wrap">
              <Text c="dimmed" size="sm">{`${release.items.length} endringer`}</Text>
            </Group>

            {release.summary ? <Text size="sm">{release.summary}</Text> : null}
          </Stack>
        </Group>

        <Stack gap="sm">
          {release.items.map((item, idx) => (
            <ReleaseItemCard key={`${dateOnly}-${idx}`} item={item} />
          ))}
        </Stack>
      </Stack>
    </Card>
  );
};
export const ReleaseNotesPublicList: React.FC = () => {
  const releases = useAppSelector(selectReleaseNotesPublishedFiltered);
  const totalCount = useAppSelector(selectReleaseNotesPublishedFilteredTotalCount);

  if (releases.length === 0) {
    return (
      <Card withBorder radius="lg" padding="md">
        <Stack gap="xs">
          <Title order={4}>Ingen treff</Title>
          <Text c="dimmed">Prøv å justere filtrene, eller nullstill for å se alle endringer.</Text>
        </Stack>
      </Card>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap">
        <Title order={4}>{`Viser ${totalCount} endringer`}</Title>
        <Text c="dimmed">{`${releases.length} release-notater`}</Text>
      </Group>

      {/* Scroll container */}
      <Card withBorder radius="lg" padding="md">
        <div
          style={{
            maxHeight: "72vh", // juster: f.eks 60vh / 70vh
            overflowY: "auto",
            overflowX: "hidden",
            paddingRight: 8, // litt luft mot scrollbar
          }}
        >
          <Stack gap="md">
            {releases.map((r) => (
              <ReleaseCard key={`${formatIsoDateOnly(r.releaseDate)}-${r.version}`} release={r} />
            ))}
          </Stack>
        </div>
      </Card>
    </Stack>
  );
};
